// Agrupa items por `branchName` (sucursal), preservando orden de aparición
// y dejando "Sin sucursal asignada" al final. Devuelve `null` si ningún
// item tiene sucursal (no hace falta agrupar). Usado tanto por el mensaje
// completo como por el fallback compacto — antes el fallback compacto
// listaba todo mezclado y perdía la separación por sucursal cuando el
// pedido era grande y superaba el límite de longitud de la URL de WhatsApp
// (bug reportado: pedidos con varias sucursales llegaban "todo mezclado").
function groupItemsByBranch(items) {
  if (!items.some((it) => (it.branchName || '').trim())) return null;
  const groups = new Map();
  items.forEach((item) => {
    const key = (item.branchName || '').trim().toLowerCase() || '__none__';
    const label = (item.branchName || '').trim() || 'Sin sucursal asignada';
    if (!groups.has(key)) groups.set(key, { label, items: [] });
    groups.get(key).items.push(item);
  });
  return Array.from(groups.entries())
    .sort((a, b) => (a[0] === '__none__' ? 1 : b[0] === '__none__' ? -1 : 0))
    .map(([, g]) => g);
}

// Construye el texto de WhatsApp para un PEDIDO EDITADO con el MISMO
// formato/estructura que el mensaje de un pedido NUEVO (ver el builder
// dentro de `CartSidebar.js`): agrupado por sucursal (si corresponde),
// precio de LISTA por línea + un único descuento agregado en el resumen
// (para que la suma de líneas + descuento cierre con el TOTAL NETO).
// Usado por AccountPage.js (cliente edita su pedido) y
// SellerDashboardPage.js (vendedor edita un pedido) para que ambos casos
// generen exactamente el mismo tipo de mensaje que un pedido nuevo.
export function buildEditedOrderMessage({
  orderNumber, isSeller, sellerName, customerName, address, city, phone,
  items, // [{ productId/id, name/productName, quantity, listUnitPrice, totalPrice, branchName, isGift, giftOriginalPrice, isComboItem, comboId, comboName, stock }]
  permanentDiscountPct = 0, cartDiscountPct = 0,
  shippingMethodName = '', shippingCost = 0,
}) {
  let message = `*PEDIDO EDITADO #${orderNumber}*\n`;
  if (isSeller) message += `*Vendedor:* ${sellerName}\n*Cliente:* ${customerName}\n`;
  else message += `*Cliente:* ${customerName}\n`;
  message += `*Dir:* ${address}, ${city}\n*Tel:* ${phone}\n\n`;

  const comboGroups = {};
  const regularItems = [];
  const giftItems = [];
  items.forEach((item) => {
    if (item.isGift) giftItems.push(item);
    else if (item.isComboItem && item.comboId) {
      if (!comboGroups[item.comboId]) comboGroups[item.comboId] = { name: item.comboName, items: [] };
      comboGroups[item.comboId].items.push(item);
    } else regularItems.push(item);
  });

  Object.values(comboGroups).forEach((combo) => {
    message += `*COMBO ${(combo.name || '').toUpperCase()}*\n`;
    let comboTotal = 0;
    combo.items.forEach((item) => {
      comboTotal += item.totalPrice || 0;
      const unit = item.quantity ? Math.round((item.totalPrice || 0) / item.quantity) : 0;
      const name = (item.name || item.productName || '').replace(` (${combo.name})`, '');
      message += `  - ${item.quantity}x ${name} $${unit.toLocaleString('es-AR')}\n`;
    });
    message += `  *Precio combo: $${Math.round(comboTotal).toLocaleString('es-AR')}*\n\n`;
  });

  const renderRegularLine = (item) => {
    const listUnit = Math.round(Number(item.listUnitPrice || 0));
    const lineTotal = listUnit * item.quantity;
    const name = item.name || item.productName || '';
    let stockNote = '';
    if (item.stock === 0) stockNote = ' ⚠️ CONFIRMAR DISPONIBILIDAD';
    else if (item.stock > 0 && item.quantity > item.stock) stockNote = ` ⚠️ (Stock: ${item.stock} - Confirmar disponibilidad)`;
    const line = `• ${item.quantity} ${name} $${listUnit.toLocaleString('es-AR')} total $${Math.round(lineTotal).toLocaleString('es-AR')}${stockNote}`;
    return { line, lineTotal };
  };

  const branchGroupsFull = groupItemsByBranch(regularItems);
  if (branchGroupsFull) {
    branchGroupsFull.forEach((group) => {
      message += `🏪 *SUCURSAL: ${group.label.toUpperCase()}*\n`;
      let branchTotal = 0;
      group.items.forEach((item) => {
        const { line, lineTotal } = renderRegularLine(item);
        branchTotal += lineTotal;
        message += `${line}\n`;
      });
      message += `  _Subtotal ${group.label}: $${Math.round(branchTotal).toLocaleString('es-AR')}_\n\n`;
    });
  } else {
    regularItems.forEach((item) => {
      const { line } = renderRegularLine(item);
      message += `${line}\n\n`;
    });
  }

  let giftSavingsTotal = 0;
  if (giftItems.length > 0) {
    giftItems.forEach((gi) => {
      const orig = Number(gi.giftOriginalPrice || 0) * Number(gi.quantity || 0);
      giftSavingsTotal += orig;
      message += `🎁 *REGALO:* ${gi.name || gi.productName} x${gi.quantity} - $0`;
      if (orig > 0) message += ` _(valor $${Math.round(orig).toLocaleString('es-AR')})_`;
      message += '\n\n';
    });
  }

  const productsBaseGross = items.reduce((sum, it) => {
    if (it.isGift) return sum;
    return sum + Number(it.listUnitPrice || 0) * (it.quantity || 0);
  }, 0);
  const productsSubtotalNet = items.reduce((s, it) => s + (it.isGift ? 0 : (it.totalPrice || 0)), 0);
  const winnerDiscountDelta = Math.round(Math.max(0, productsBaseGross - productsSubtotalNet));

  message += `*Subtotal base:* $${Math.round(productsBaseGross).toLocaleString('es-AR')}\n`;
  if (winnerDiscountDelta > 0) {
    const useCartTierLabel = cartDiscountPct > 0 && cartDiscountPct >= permanentDiscountPct;
    if (useCartTierLabel) {
      message += `*🎯 Descuento por objetivo de carrito (${cartDiscountPct}%):* -$${winnerDiscountDelta.toLocaleString('es-AR')}\n`;
    } else if (permanentDiscountPct > 0) {
      message += `*✨ Descuento permanente cliente (${permanentDiscountPct}%):* -$${winnerDiscountDelta.toLocaleString('es-AR')}\n`;
    } else {
      message += `*🎯 Descuento aplicado:* -$${winnerDiscountDelta.toLocaleString('es-AR')}\n`;
    }
  }
  message += '\n';

  const totalSavings = Math.round(Math.max(0, productsBaseGross - productsSubtotalNet) + giftSavingsTotal);
  if (totalSavings > 0) {
    const savingsPct = productsBaseGross > 0 ? Math.round((totalSavings / productsBaseGross) * 100) : 0;
    message += `*💰 Ahorro total:* $${totalSavings.toLocaleString('es-AR')}`;
    if (giftSavingsTotal > 0) message += ` _(incluye $${Math.round(giftSavingsTotal).toLocaleString('es-AR')} en regalos)_`;
    message += '\n';
    if (savingsPct > 0) message += `*🎉 Ahorraste un ${savingsPct}% en esta compra*\n`;
  }

  message += '━━━━━━━━━━━━━━━━━━━━\n';
  message += `*MÉTODO DE ENVÍO:*\n*${shippingMethodName || 'A coordinar'}*`;
  if (shippingCost > 0) message += ` - $${Math.round(shippingCost).toLocaleString('es-AR')}`;
  message += '\n━━━━━━━━━━━━━━━━━━━━\n\n';

  const finalTotal = Math.round(productsSubtotalNet + (shippingCost || 0));
  message += `*TOTAL NETO: $${finalTotal.toLocaleString('es-AR')}*`;
  return { message, finalTotal };
}

// Fallback compacto (mismo criterio que el pedido nuevo en CartSidebar.js):
// si el link de WhatsApp supera el límite de longitud del navegador,
// mostramos TODOS los productos con precio de lista por línea pero sin el
// resumen de descuentos detallado, para no perder ítems del pedido.
// 🏪 Sigue agrupando por sucursal si corresponde — antes este fallback
// listaba todo mezclado y era la causa de pedidos grandes llegando sin
// separar por sucursal.
export function buildCompactOrderMessage({ orderNumber, isSeller, sellerName, customerName, address, city, phone, items, finalTotal }) {
  let msg = `*PEDIDO EDITADO #${orderNumber}*\n`;
  if (isSeller) msg += `*Vendedor:* ${sellerName}\n*Cliente:* ${customerName}\n`;
  else msg += `*Cliente:* ${customerName}\n`;
  msg += `*Dir:* ${address}, ${city}\n*Tel:* ${phone}\n\n`;
  const renderLine = (item) => {
    const name = item.name || item.productName || '';
    const unit = Math.round(Number(item.listUnitPrice || 0));
    return `${item.quantity}x ${name} $${unit.toLocaleString('es-AR')} total $${Math.round(unit * item.quantity).toLocaleString('es-AR')}\n`;
  };
  const branchGroups = groupItemsByBranch(items);
  if (branchGroups) {
    branchGroups.forEach((group) => {
      msg += `🏪 *SUCURSAL: ${group.label.toUpperCase()}*\n`;
      let branchTotal = 0;
      group.items.forEach((item) => {
        if (!item.isGift) branchTotal += Math.round(Number(item.listUnitPrice || 0)) * item.quantity;
        msg += renderLine(item);
      });
      msg += `  _Subtotal ${group.label}: $${Math.round(branchTotal).toLocaleString('es-AR')}_\n\n`;
    });
  } else {
    items.forEach((item) => { msg += renderLine(item); });
  }
  msg += `\n*TOTAL: $${Math.round(finalTotal).toLocaleString('es-AR')}*`;
  return msg;
}

// Abre WhatsApp con el mensaje completo; si el link resulta demasiado largo
// (límite práctico de los navegadores/OS para deep-links), usa el mensaje
// compacto; si aun así es muy largo, copia el mensaje completo al
// portapapeles y abre WhatsApp sin texto prellenado.
export function openWhatsappWithFallback({ fullMessage, compactMessage, whatsappNumber }) {
  const MAX_URL_LENGTH = 4000;
  const baseUrl = `https://wa.me/${whatsappNumber}`;
  let url = `${baseUrl}?text=${encodeURIComponent(fullMessage)}`;
  if (url.length > MAX_URL_LENGTH) {
    url = `${baseUrl}?text=${encodeURIComponent(compactMessage)}`;
  }
  if (url.length > MAX_URL_LENGTH) {
    try { navigator.clipboard?.writeText(fullMessage); } catch (_) { /* ignore */ }
    window.open(baseUrl, '_blank');
    return { copiedToClipboard: true };
  }
  window.open(url, '_blank');
  return { copiedToClipboard: false };
}
